// Auth/forms/AuthForm.tsx

import React, {ChangeEvent, useContext, useState} from 'react';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import 'react-phone-input-2/lib/material.css'
import SocialOAuth from "Auth/Social/components/SocialOAuth";
import {axios} from "Auth/axiosConfig";
import TextField from "@mui/material/TextField";
import pprint from "../../../Utils/pprint";
import ConfirmationCode, {ConfirmationMethod} from "Confirmation/ConfirmationCode";
import Button from "Core/components/elements/Button/Button";
import PhoneField from "Core/components/elements/PhoneField/PhoneField";
import {FC, FCC} from "WideLayout/Layouts";
import {Message} from "Core/components/Message";
import {isEmail, isPhone} from "../../../Utils/validator/base";
import {useTheme} from "Theme/ThemeContext";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import SignUpForm from "Auth/forms/SignUpForm";
import BackButton from "Core/components/BackButton";

const AuthForm: React.FC = () => {
    const {isAuthenticated, login, handleAuthResponse} = useContext(AuthContext) as AuthContextType;
    const {byResponse} = useErrorProcessing();
    const [credential, setCredential] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);  // 1 - Enter credential, 2 - Choose login method, 3 - SignUpForm, 4 - ConfirmationCode
    const [password, setPassword] = useState<string>('');
    const [selectedTab, setSelectedTab] = useState<number>(0);  // 0 - Phone, 1 - Email
    const [isValidCredential, setIsValidCredential] = useState<boolean>(false);
    const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean>(false);
    const [isPhoneConfirmed, setIsPhoneConfirmed] = useState<boolean>(false);
    const [isPasswordExists, setIsPasswordExists] = useState<boolean>(false);
    const [confirmationMethod, setConfirmationMethod] = useState<ConfirmationMethod | null>(null);
    const [useConfirmation, setUseConfirmation] = useState<boolean>(false);
    const [confirmationAction, setConfirmationAction] = useState<string>('auth'); // 'auth' or 'signup'
    const {plt} = useTheme();

    const [initialCodeSent, setInitialCodeSent] = useState<boolean>(false); // Новое состояние

    const handlePasswordLogin = () => {
        setLoading(true);
        login(credential, password)
            .then(r => setLoading(false))
            .catch(() => setLoading(false));
    };

    const handleCredentialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCredential(e.target.value);
        validateCredential(e.target.value);
    };

    const handlePhoneChange = (value: string) => {
        setCredential(value);
        validateCredential(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isValidCredential) {
            handleNextStep(e);
        }
    };

    const validateCredential = (value: string) => {
        if (selectedTab === 0) {
            const isPhoneValid = isPhone(value);
            setIsValidCredential(isPhoneValid);
            if (isPhoneValid) setConfirmationMethod('phone');
        } else {
            const isEmailValid = isEmail(value);
            setIsValidCredential(isEmailValid);
            if (isEmailValid) setConfirmationMethod('email');
        }
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setStep(1);
        setSelectedTab(newValue);
        setCredential('');
        setIsValidCredential(false);
    };

    const handleTabKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Tab' && step === 1) {
            e.preventDefault();
            setSelectedTab((prevTab) => (prevTab === 0 ? 1 : 0));
            setStep(1);
        }
    };

    const handleToggleTab = () => {
        if (step === 1) {
            setSelectedTab((prevTab) => (prevTab === 0 ? 1 : 0));
            setStep(1);
        }
    };

    const handleNextStep = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post('/api/v1/user/auth_methods/', {credential});
            pprint(`Auth Methods by ${credential}`);
            pprint(response.data);
            if (response.data.user_not_exists) {
                setStep(3); // Step 3: SignUpForm
            } else {
                setIsEmailConfirmed(response.data.is_email_confirmed);
                setIsPhoneConfirmed(response.data.is_phone_confirmed);
                setIsPasswordExists(response.data.password_exists);
                if (!response.data.password_exists &&
                    (response.data.is_email_confirmed || response.data.is_phone_confirmed)) {
                    Message.info(
                        'Установите пароль после входа, чтобы в следующий раз была возможность войти по паролю',
                        6000
                    );
                }
                if (!response.data.is_email_confirmed && !response.data.is_phone_confirmed) {
                    setConfirmationAction('signup');
                    setInitialCodeSent(true);
                    setStep(4);
                } else {
                    setStep(2);
                }
            }
        } catch (error) {
            byResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmWithCode = (method: ConfirmationMethod) => {
        setConfirmationMethod(method);
        setConfirmationAction('auth');
        setUseConfirmation(true);
    };

    const handleSignupSuccess = (credential: string, confirmationMethod: ConfirmationMethod) => {
        setCredential(credential);
        setConfirmationMethod(confirmationMethod);
        setConfirmationAction('signup');
        setInitialCodeSent(true);
        setStep(4);
    };

    const onConfirmAuth = (action_result: any) => {
        pprint('onConfirmAuth', action_result);
        handleAuthResponse(action_result.result, '/consultations');
    };

    const handleResend = () => {
        setInitialCodeSent(false);
    };

    if (isAuthenticated === null) return null;

    return (
        <FC w={'100%'} onKeyDown={handleTabKeyPress}>
            {step === 1
                ? ''
                : <BackButton
                    onClick={() => {
                        setStep(1);
                        setUseConfirmation(false);
                    }}
                />
            }

            {step === 1 && (
                <form className={`fcc gap-2 pt-3`} onSubmit={handleNextStep} noValidate>
                    {selectedTab === 0
                        ? <PhoneField
                            cls={'mt-1'}
                            phone={credential}
                            onReturn={handleKeyDown}
                            onChange={handlePhoneChange}
                            disabled={false}
                        />
                        : <TextField
                            label="Почта"
                            variant="outlined"
                            type="email"
                            autoFocus={true}
                            value={credential}
                            onChange={handleCredentialChange}
                            fullWidth
                            className={''}
                        />
                    }
                    <Button onClick={handleToggleTab}>
                        Сменить метод авторизации
                    </Button>
                    <Button
                        type="submit"
                        color={'primary'}
                        disabled={!isValidCredential || loading}
                        loading={loading}>
                        Далее
                    </Button>
                </form>
            )}

            {step === 2 &&
                <FCC g={1} mt={1} pt={1}>
                    {selectedTab === 0
                        ? <PhoneField
                            cls={'mt-1'}
                            phone={credential}
                            onReturn={handleKeyDown}
                            onChange={handlePhoneChange}
                            disabled={true}
                        />
                        : <TextField
                            label="Почта"
                            variant="outlined"
                            type="email"
                            value={credential}
                            onChange={handleCredentialChange}
                            fullWidth
                            disabled
                            className={'opacity-50 pointer-events-none'}
                        />
                    }

                    {useConfirmation && confirmationMethod
                        ?
                        <ConfirmationCode
                            credential={credential}
                            confirmationMethod={confirmationMethod}
                            action={confirmationAction}
                            autoFocus={true}
                            onConfirm={onConfirmAuth}
                        />
                        : (isEmailConfirmed || isPhoneConfirmed) && <>
                        {isPasswordExists && <>
                            <TextField
                                autoFocus={true}
                                label="Пароль"
                                variant='outlined'
                                type="password"
                                value={password}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && password) {
                                        handlePasswordLogin();
                                    }
                                }}
                                fullWidth
                                margin="none"
                            />
                            <Button
                                color={'primary'}
                                onClick={handlePasswordLogin}
                                loading={loading}>
                                Войти по паролю
                            </Button>
                        </>}
                        {isPhoneConfirmed && (
                            <Button color={'primary'}
                                    autoFocus={!isPasswordExists}
                                    onClick={() => handleConfirmWithCode('phone')}
                                    loading={loading}>
                                Код на телефон
                            </Button>
                        )}
                        {isEmailConfirmed && (
                            <Button
                                color={'primary'}
                                onClick={() => handleConfirmWithCode('email')}
                                loading={loading}>
                                Код на почту
                            </Button>
                        )}
                    </>
                    }
                </FCC>
            }
            {step === 3 && (
                <SignUpForm className={'mt-3'}
                            credential={credential}
                            onSignupSuccess={handleSignupSuccess}/>
            )}
            {step === 4 && confirmationMethod && (
                <ConfirmationCode
                    className={'mt-3'}
                    credential={credential}
                    confirmationMethod={confirmationMethod}
                    action={confirmationAction}
                    initialCodeSent={initialCodeSent}
                    onConfirm={onConfirmAuth}
                    onResend={handleResend}
                />
            )}

            <SocialOAuth className={'frcc mt-2'}/>
        </FC>
    );
};

export default AuthForm;
